/**
 * @fileoverview Main Router class for the OPUS 2.0 modular module loading system.
 * 
 * This file contains the central Router orchestrator that coordinates all module loaders
 * in a modular, scalable architecture. The Router follows the singleton pattern and
 * manages seven specialized loaders for different types of modules.
 * 
 * @author Roshan Jose
 * @version 2.0.0
 * @since 2025
 */

import { BrandJsonLoader } from "./modulePaths/brand-json-loader";
import { BrandLoader } from "./modulePaths/brand-loader";
import { ConditionalPageLoader } from "./modulePaths/conditional-page-loader";
import { DomLoader } from "./modulePaths/dom-loader";
import { FeatureModule } from "./modulePaths/feature-module";
import { RequiredLoader } from "./modulePaths/required-loader";
import { TemplateLoader } from "./modulePaths/template-loader";
import { RouterHelper } from "./router-helper";
import { 
    IRouter, 
    IModuleLoader, 
    IBrandLoader, 
    ITemplateLoader, 
    IBrandJsonLoader, 
    IConditionalLoader,
    RouterStatus 
} from "./interfaces/module-loader.interfaces";

/**
 * Main Router class that orchestrates all module loading operations.
 * 
 * The Router implements a modular architecture with seven specialized loaders:
 * - RequiredLoader: Global modules needed on every page
 * - TemplateLoader: Page template-specific modules
 * - DomLoader: DOM manipulation and UI modules
 * - BrandLoader: Brand-specific customization modules
 * - BrandJsonLoader: Dynamic brand configuration modules
 * - FeatureModule: Browser feature detection modules
 * - ConditionalPageLoader: Page condition-based modules
 * 
 * Features:
 * - Singleton pattern for global access
 * - Promise.allSettled for resilient loading
 * - Comprehensive error handling and logging
 * - Race condition prevention
 * - Timeout protection (via RouterHelper)
 * - Detailed status reporting
 * 
 * @class Router
 * @implements {IRouter}
 */
export class Router implements IRouter {
    /** Singleton instance of the Router */
    private static instance: Router;
    
    /** RouterHelper instance for secure module loading operations */
    private readonly routerHelper: RouterHelper = RouterHelper.getInstance();
    
    /** Brand-specific module loader for customizations and brand features */
    private readonly brandLoader: IBrandLoader = BrandLoader.getInstance();
    
    /** Required module loader for global modules needed on every page */
    private readonly requiredLoader: IModuleLoader = RequiredLoader.getInstance();
    
    /** DOM module loader for UI manipulation and DOM-related functionality */
    private readonly domLoader: IModuleLoader = DomLoader.getInstance();
    
    /** Template module loader for page template-specific modules */
    private readonly templateLoader: ITemplateLoader = TemplateLoader.getInstance();
    
    /** Brand JSON module loader for dynamic brand configuration */
    private readonly brandJsonLoader: IBrandJsonLoader = BrandJsonLoader.getInstance();
    
    /** Feature module loader for browser capability detection */
    private readonly featureModule: IConditionalLoader = FeatureModule.getInstance();
    
    /** Conditional page module loader for page-specific requirements */
    private readonly conditionalPageLoader: IConditionalLoader = ConditionalPageLoader.getInstance();
    
    /** Array of all loaders for uniform processing and orchestration */
    private readonly loaders: IModuleLoader[] = [
        this.requiredLoader,
        this.templateLoader,
        this.domLoader,
        this.brandLoader,
        this.brandJsonLoader,
        this.featureModule,
        this.conditionalPageLoader
    ];

    /**
     * Gets the singleton instance of the Router.
     * Creates a new instance if one doesn't exist.
     * @returns The singleton Router instance
     */
    public static getInstance(): Router {
        this.instance ??= new Router();
        return this.instance;
    }
    
    /**
     * Private constructor to enforce singleton pattern.
     * Automatically initializes the router upon instantiation.
     */
    constructor() {
        this.init();
    }

    /**
     * Initializes the router system and starts the module loading process.
     * 
     * This method kicks off the asynchronous module loading process and sets up
     * success/error logging. It's called automatically by the constructor.
     */
    init(): void {
        this.routerModules().then(() => {
            this.routerHelper.routerConsoleLog('Router loaded successfully');
        }).catch(error => {
            this.routerHelper.routerConsoleLog( `Router failed to load - ${error}`, true);
        });
    }

    /**
     * Orchestrates the complete module loading lifecycle.
     * 
     * This is the main coordination method that:
     * 1. Initializes all loaders in parallel
     * 2. Loads all modules across all loaders in parallel
     * 
     * Uses Promise.allSettled to ensure resilient loading where individual
     * loader failures don't prevent other loaders from completing.
     * 
     * @returns Promise that resolves when all loading operations complete
     */
    async routerModules(): Promise<void> {
        // Initialize all loaders first
        await this.initializeLoaders();
        
        // Load modules using uniform interface
        await this.loadAllModules();
    }

    /**
     * Initializes all loaders uniformly in parallel.
     * 
     * Each loader's init() method is called concurrently using Promise.allSettled
     * to ensure that individual initialization failures don't prevent other
     * loaders from initializing successfully.
     * 
     * @private
     * @returns Promise that resolves when all initialization attempts complete
     */
    private async initializeLoaders(): Promise<void> {
        const initPromises = this.loaders.map(async (loader) => {
            try {
                await loader.init();
                this.routerHelper.routerConsoleLog(`${loader.getName()} initialized successfully`, false, loader.getName());
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                this.routerHelper.routerConsoleLog(`${loader.getName()} initialization failed - ${errorMessage}`, true, loader.getName());
            }
        });
        
        await Promise.allSettled(initPromises);
    }

    /**
     * Loads all modules across all loaders uniformly in parallel.
     * 
     * Each loader's loadModules() method is called concurrently using Promise.allSettled
     * to ensure that individual loading failures don't prevent other loaders from
     * completing their module loading successfully.
     * 
     * @private
     * @returns Promise that resolves when all module loading attempts complete
     */
    private async loadAllModules(): Promise<void> {
        const loadPromises = this.loaders.map(async (loader) => {
            try {
                await loader.loadModules();
                this.routerHelper.routerConsoleLog(`${loader.getName()} modules loaded successfully`, false, loader.getName());
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                this.routerHelper.routerConsoleLog(`${loader.getName()} module loading failed - ${errorMessage}`, true, loader.getName());
            }
        });
        
        await Promise.allSettled(loadPromises);
    }

    /**
     * Loads conditional modules that depend on runtime conditions.
     * 
     * This method handles modules that should only be loaded based on:
     * - Browser feature availability (via FeatureModule)
     * - Page-specific conditions (via ConditionalPageLoader)
     * 
     * These modules are loaded sequentially after the main module loading
     * process to ensure dependencies are available.
     * 
     * @returns Promise that resolves when conditional modules are loaded
     */
    async conditionalModules(): Promise<void> {
        // Load conditional modules using interface methods
        await this.featureModule.loadConditionalFeatureModules();
        await this.conditionalPageLoader.loadConditionalPageModules();
    }

    /**
     * Gets comprehensive status information for the entire router system.
     * 
     * Aggregates status information from all loaders to provide a complete
     * picture of the router's state, including:
     * - Total number of loaders
     * - Count of initialized, loading, completed, and failed loaders
     * - Detailed status for each individual loader
     * 
     * @returns Complete router system status information
     */
    getModuleStatus(): RouterStatus {
        const loaderStatuses: { [key: string]: any } = {};
        let initializedCount = 0;
        let loadingCount = 0;
        let completedCount = 0;
        let failedCount = 0;

        for (const loader of this.loaders) {
            const status = loader.getStatus();
            const name = loader.getName();
            loaderStatuses[name] = status;

            if (status.isInitialized) initializedCount++;
            if (status.isLoading) loadingCount++;
            if (status.loadedModules.length > 0) completedCount++;
            if (status.failedModules.length > 0) failedCount++;
        }

        return {
            totalLoaders: this.loaders.length,
            initializedLoaders: initializedCount,
            loadingLoaders: loadingCount,
            completedLoaders: completedCount,
            failedLoaders: failedCount,
            loaderStatuses
        };
    }
}

